import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControlName, FormGroupName } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@Directive({
    selector: '[requiredMarker]',
    standalone: true,
})
export class RequiredMarkerDirective implements OnChanges, OnInit
{
    @Input() requiredMarker: boolean;
    private markerRef: HTMLSpanElement;

    constructor(
            private _el: ElementRef,
            private _renderer: Renderer2
    ) {
    }

    ngOnChanges({ requiredMarker }: SimpleChanges) {
        if (requiredMarker) {
            if (requiredMarker.currentValue === '') {
                this.requiredMarker = true;
            }
            this.updateMarker();
        }
    }

    ngOnInit() {
        this.updateMarker();
    }

    private updateMarker() {
        if (this.markerRef) {
            this._renderer.removeChild(this._el.nativeElement, this.markerRef);
            this.markerRef = null;
        }

        if (this.requiredMarker) {
            const span: HTMLSpanElement = this._renderer.createElement('span');
            span.classList.add('required-marker');
            span.textContent = '*';

            this.markerRef = span;
            this._renderer.appendChild(this._el.nativeElement, this.markerRef);
        }
    }
}

/**
 * Directive that applies readonly styling to Material form fields.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .form-field-readonly
 */
@Directive({
    selector: '[readonlyField]',
    standalone: true,
})
export class ReadonlyFieldDirective {
    constructor(
        private _matFormField: MatFormField
    ) {
    }

    @HostBinding('class.form-field-readonly') get isReadonlyInput() {
        const control = this._matFormField._control;
        return control instanceof MatInput && control.readonly;
    }
}

/**
 * Directive that applies flex layout to mat-select-trigger content when
 * displaying multiple elements side-by-side (e.g., text + spinner, text + icon).
 * Not needed for single text-only triggers.
 *
 * @example
 * <mat-select-trigger>
 *     <span selectTriggerContent>{{ 'Loading...' }} <mat-spinner [diameter]="18"></mat-spinner></span>
 * </mat-select-trigger>
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .select-trigger-content
 */
@Directive({
    selector: '[selectTriggerContent]',
    standalone: true,
    host: { class: 'select-trigger-content' },
})
export class SelectTriggerContentDirective
{
}

/**
 * Directive that applies a stacked (multi-line) layout to mat-option elements.
 * Use when an option needs vertically stacked content instead of the default
 * single-line display. Pair with `.description-text` for secondary detail lines.
 *
 * @example
 * <mat-option stacked [value]="item.id">
 *     <span>{{ item.name }}</span>
 *     <span class="description-text">{{ item.detail }}</span>
 * </mat-option>
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .stacked-option
 */
@Directive({
    selector: 'mat-option[stacked]',
    standalone: true,
    host: { class: 'stacked-option' },
})
export class StackedOptionDirective
{
}

@UntilDestroy()
@Directive({
    selector: 'mat-option[selectAll]',
    standalone: true,
})
export class SelectAllDirective<T> implements AfterViewInit
{
    @Input() allValues: T[] = [];
    @Output() selectedCountChange = new EventEmitter<number>();

    constructor(
            private _matSelect: MatSelect,
            private _matOption: MatOption<T>,
    ) {
    }

    ngAfterViewInit() {
        const parentFormControl = this._matSelect.ngControl.control;
        if (!parentFormControl) {
            console.warn('Unable to find parent control to attach select all option');
            return;
        }

        // utilizing null for select-all in multiple select list (vs. forcing to any/incorrect type)
        this._matOption.value = null;

        // handle when the select checkbox is toggled
        this._matOption.onSelectionChange.pipe(untilDestroyed(this)).subscribe({
            next: (value) => {
                if (value.isUserInput) {
                    if (value.source.selected) {
                        parentFormControl.setValue(this.allValues);
                        this._matOption.select();
                        this.selectedCountChange.emit(this.allValues.length);
                    } else {
                        parentFormControl.setValue([]);
                        this._matOption.deselect();
                        this.selectedCountChange.emit(0);
                    }
                }
            },
        });

        // handle when an individual option is toggled
        this._matSelect.optionSelectionChanges.pipe(untilDestroyed(this)).subscribe({
            next: (value) => {
                if (value.isUserInput && value.source.value !== this._matOption.value) {
                    if (!value.source.selected) {
                        this._matOption.deselect();
                    } else {
                        if (parentFormControl.value.length === this.allValues.length) {
                            this._matOption.select();
                        }
                    }
                }
            },
        });

        // on open and close of select panel, toggle "select all" to avoid unnecessary null form control value in selected array
        this._matSelect.openedChange.pipe(untilDestroyed(this)).subscribe({
            next: (opened) => {
                if (opened && parentFormControl.value.length === this.allValues.length) {
                    this._matOption.select();
                } else if (!opened && this._matOption.selected) {
                    this._matOption.deselect();
                }
            },
        });
    }
}

/**
 * Directive that tracks form control focus and disabled states.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss and _v2_cards.scss
 * - .control-disabled
 * - .control-focused
 */
@UntilDestroy()
@Directive({
    selector: '[formNameAccessor]',
    standalone: true,
})
export class FormAccessorDirective implements OnInit
{
    @Input() focusInputRef: HTMLInputElement;

    @HostBinding('class.control-disabled') disabledClass = false;
    @HostBinding('class.control-focused') hasFocus = false;
    @HostBinding('class.control-invalid') invalidClass = false;

    constructor(
        @Optional() @Inject(FormGroupName) private formGroupName: FormGroupName,
        @Optional() @Inject(FormControlName) private formControlName: FormControlName,
    ) {
    }

    @HostListener('focusin', ['$event']) onFocus(event: FocusEvent): void {
        const el = event.target as HTMLElement;
        this.hasFocus = el?.id === this.focusInputRef?.id;
    }

    @HostListener('focusout', ['$event']) onFocusOut(event: FocusEvent): void {
        this.hasFocus = false;
    }

    ngOnInit() {
        let control: FormGroupName|FormControlName;
        if (this.formControlName) {
            control = this.formControlName;
        } else if (this.formGroupName) {
            control = this.formGroupName;
        }

        if (control) {
            control.statusChanges.pipe(untilDestroyed(this)).subscribe({
                next: () => {
                    this.disabledClass = control.disabled;
                    this.invalidClass = this.focusInputRef?.id && control.invalid && (control.touched || control.dirty);
                },
            });
            this.disabledClass = control.disabled;
            this.invalidClass =  this.focusInputRef?.id && control.invalid && (control.touched || control.dirty);
        }
    }
}

/**
 * Directive that styles labels below form fields (subscript position).
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .form-label
 */
@Directive({
    selector: '[subscriptLabel]',
    standalone: true,
    host: { class: 'form-label' },
})
export class SubscriptLabelDirective
{
}

/**
 * Directive that adds a character count display to a label element.
 *
 * Sets the host to `display: flex` and appends a right-aligned count span
 * when active. No external CSS class or styles needed.
 *
 * @example
 * <label characterCount [countControl]="formGroup.controls.nameOnCard" [maxLength]="24">
 *     {{ 'Name on card' }}
 * </label>
 */
@UntilDestroy()
@Directive({
    selector: '[characterCount]',
    standalone: true,
})
export class CharacterCountDirective implements OnInit
{
    @Input() countControl: AbstractControl<string>;
    @Input() maxLength: number;
    @Input() showCount = true;

    @HostBinding('style.display') get hostDisplay(): string | null {
        return this.showCount ? 'flex' : null;
    }

    private _countRef: HTMLSpanElement;

    constructor(
        private _el: ElementRef,
        private _renderer: Renderer2,
    ) {}

    ngOnInit(): void {
        if (!this.showCount || !this.countControl || !this.maxLength) {
            return;
        }

        this._countRef = this._renderer.createElement('span');
        this._renderer.setStyle(this._countRef, 'margin-left', 'auto');
        this._renderer.setStyle(this._countRef, 'font-weight', '400');
        this._renderer.setStyle(this._countRef, 'opacity', '0.6');
        this._updateCount();
        this._renderer.appendChild(this._el.nativeElement, this._countRef);

        this.countControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this._updateCount());
        this.countControl.statusChanges.pipe(untilDestroyed(this)).subscribe(() => this._updateCount());
    }

    private _updateCount(): void {
        if (!this._countRef) {
            return;
        }
        const length = this.countControl.value?.length ?? 0;
        const formattedLength = length.toLocaleString();
        const formattedMax = this.maxLength.toLocaleString();
        this._countRef.textContent = `${formattedLength}/${formattedMax}`;

        const isInvalid = this.countControl.invalid && (this.countControl.touched || this.countControl.dirty);
        this._renderer.setStyle(this._countRef, 'color', isInvalid ? '#E11D48' : '');
        this._renderer.setStyle(this._countRef, 'opacity', isInvalid ? '1' : '0.6');
    }
}

/**
 * Directive that styles radio button option titles.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .radio-label-title
 */
@Directive({
    selector: '[radioTitle]',
    standalone: true,
    host: { class: 'radio-label-title' },
})
export class RadioTitleDirective
{
}

/**
 * Directive that styles radio button option descriptions.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .radio-label-description
 */
@Directive({
    selector: '[radioDescription]',
    standalone: true,
    host: { class: 'radio-label-description' },
})
export class RadioDescriptionDirective
{
}

/**
 * Directive that styles Material radio groups with v2-layout patterns.
 * Supports button style, stacked layout, and inverse color schemes.
 *
 * @styles /ui/src/styles/ui/mixins/_v2_forms.scss
 * - .radio-group
 * - .radio-group-buttons
 * - .radio-group-stacked
 * - .radio-group-inverse
 */
@Directive({
    selector: 'mat-radio-group[radioGroup]',
    standalone: true,
    host: { class: 'radio-group' },
    hostDirectives: [{
        directive: FormAccessorDirective,
    }],
})
export class RadioGroupDirective
{
    @Input() radioStyle: 'button' | null;
    @Input() radioStacked = false;
    @Input() radioInverse = false;

    @HostBinding('class.radio-group-buttons') get isRadioControlButtonStyle() {
        return !!this.radioStyle && this.radioStyle === 'button';
    }

    @HostBinding('class.radio-group-stacked') get isGroupStacked() {
        return !!this.radioStacked;
    }

    @HostBinding('class.radio-group-inverse') get isRadioControlInverse() {
        return !!this.radioInverse;
    }
}
